import React, { useCallback, useEffect, useState } from 'react'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { Quote, Loader2, AlertCircle, Trash2, Eye, CheckCircle2, Archive, FileEdit, Languages } from 'lucide-react'
import {
  listTestimonialsAdmin,
  getTestimonialStats,
  changeTestimonialStatus,
  deleteTestimonial,
  TESTIMONIAL_ROLE_LABELS,
  TESTIMONIAL_STATUS_LABELS,
  type Testimonial,
  type TestimonialStatus,
} from '@/services/testimonialService'
import { TranslationEditorModal, type TranslationFieldDef } from '@/components/admin/TranslationEditorModal'
import { type TranslationRow } from '@/services/translationService'

const STATUS_FILTERS: (TestimonialStatus | 'ALL')[] = ['ALL', 'PENDING_REVIEW', 'DRAFT', 'PUBLISHED', 'ARCHIVED']

const statusTone: Record<TestimonialStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PENDING_REVIEW: 'bg-amber-100 text-amber-800',
  PUBLISHED: 'bg-green-100 text-green-700',
  ARCHIVED: 'bg-slate-100 text-slate-600',
}

const TestimonialsReview = React.memo(() => {
  const [items, setItems] = useState<Testimonial[]>([])
  const [stats, setStats] = useState<{ TOTAL: number; DRAFT: number; PENDING_REVIEW: number; PUBLISHED: number; ARCHIVED: number }>({ TOTAL: 0, DRAFT: 0, PENDING_REVIEW: 0, PUBLISHED: 0, ARCHIVED: 0 })
  const [statusFilter, setStatusFilter] = useState<TestimonialStatus | 'ALL'>('ALL')
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actingId, setActingId] = useState<number | null>(null)
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null)
  const [viewing, setViewing] = useState<Testimonial | null>(null)
  const [translateId, setTranslateId] = useState<number | null>(null)
  const size = 15

  const TRANSLATE_FIELDS: TranslationFieldDef[] = [
    { key: 'title', label: 'Title', type: 'text', required: true },
    { key: 'body', label: 'Story', type: 'textarea', rows: 8 },
  ]

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const [data, s] = await Promise.all([
        listTestimonialsAdmin({ status: statusFilter === 'ALL' ? undefined : statusFilter, page, size }),
        getTestimonialStats(),
      ])
      setItems(data.content); setTotalPages(data.totalPages); setStats(s)
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load testimonials.')
    } finally { setLoading(false) }
  }, [statusFilter, page])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(0) }, [statusFilter])

  const flash = (tone: 'success' | 'error', msg: string) => { setToast({ tone, msg }); setTimeout(() => setToast(null), 3000) }

  const changeStatus = async (t: Testimonial, status: TestimonialStatus) => {
    setActingId(t.id)
    try { await changeTestimonialStatus(t.id, status); flash('success', `“${t.title}” → ${TESTIMONIAL_STATUS_LABELS[status]}`); await load() }
    catch (e: any) { flash('error', e?.response?.data?.message || 'Failed.') }
    finally { setActingId(null) }
  }

  const remove = async (t: Testimonial) => {
    if (!window.confirm(`Delete “${t.title}”?`)) return
    setActingId(t.id)
    try { await deleteTestimonial(t.id); flash('success', 'Testimonial deleted'); await load() }
    catch (e: any) { flash('error', e?.response?.data?.message || 'Failed.') }
    finally { setActingId(null) }
  }

  return (
    <GovLayout showSidebar={false}>
      <div className="space-y-6">
        <GovPageTitle title="Testimonials" subtitle="Review consented patient and community stories before publishing" icon={Quote} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <GovCard className="text-center"><p className="text-2xl font-bold text-gray-900">{stats.TOTAL}</p><p className="text-sm text-gray-500">Total</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-amber-600">{stats.PENDING_REVIEW}</p><p className="text-sm text-gray-500">Pending</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-green-600">{stats.PUBLISHED}</p><p className="text-sm text-gray-500">Published</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-slate-500">{stats.ARCHIVED}</p><p className="text-sm text-gray-500">Archived</p></GovCard>
        </div>

        <GovCard>
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_FILTERS.map((f) => (
              <button key={f} onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {f === 'ALL' ? 'All' : TESTIMONIAL_STATUS_LABELS[f]}
              </button>
            ))}
          </div>
        </GovCard>

        {toast && (
          <div role="status" className={`flex items-center gap-2 p-3 rounded-lg text-sm ${toast.tone === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {toast.tone === 'success' ? '✓' : <AlertCircle className="w-4 h-4" />} {toast.msg}
          </div>
        )}

        <GovCard>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-900">Stories</h2>
            <button onClick={load} disabled={loading} className="text-xs text-blue-600 hover:underline disabled:opacity-50">{loading ? 'Loading…' : 'Refresh'}</button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
          ) : error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm"><AlertCircle className="w-4 h-4" /> {error}</div>
          ) : items.length === 0 ? (
            <GovEmptyState icon="💬" title="No testimonials" description="No stories match this filter. Submissions from the portal appear here as Pending review." />
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((t) => (
                <div key={t.id} className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusTone[t.status]}`}>{TESTIMONIAL_STATUS_LABELS[t.status]}</span>
                        {t.authorRole && <span className="text-xs text-gray-500">{TESTIMONIAL_ROLE_LABELS[t.authorRole]}</span>}
                        {t.isAnonymous && <span className="text-xs text-gray-400">· anonymous</span>}
                        {t.isFeatured && <span className="text-xs text-teal-600">· ★ featured</span>}
                      </div>
                      <h4 className="font-medium text-gray-900">{t.title}</h4>
                      <p className="text-xs text-gray-400">
                        {t.isAnonymous ? 'Anonymous' : (t.authorName ?? 'No name')}
                        {t.location && !t.isAnonymous ? ` · ${t.location}` : ''}
                        {t.createdAt && ` · ${new Date(t.createdAt).toLocaleDateString()}`}
                      </p>
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{t.body}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                      {actingId === t.id && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                      <button onClick={() => setViewing(t)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md" aria-label="View"><Eye className="w-4 h-4" /></button>
                      <button onClick={() => setTranslateId(t.id)} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md" aria-label="Translate"><Languages className="w-4 h-4" /></button>
                      {t.status !== 'PUBLISHED' && (
                        <GovButton variant="ghost" size="sm" onClick={() => changeStatus(t, 'PUBLISHED')} icon={<CheckCircle2 className="w-3.5 h-3.5" />}>Publish</GovButton>
                      )}
                      {t.status === 'PUBLISHED' && (
                        <GovButton variant="ghost" size="sm" onClick={() => changeStatus(t, 'ARCHIVED')} icon={<Archive className="w-3.5 h-3.5" />}>Archive</GovButton>
                      )}
                      {t.status === 'PENDING_REVIEW' && (
                        <GovButton variant="ghost" size="sm" onClick={() => changeStatus(t, 'DRAFT')} icon={<FileEdit className="w-3.5 h-3.5" />}>Draft</GovButton>
                      )}
                      <button onClick={() => remove(t)} disabled={actingId === t.id} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Previous</GovButton>
              <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</GovButton>
            </div>
          )}
        </GovCard>

        {viewing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" onClick={() => setViewing(null)}>
            <div className="bg-white rounded-xl max-w-xl w-full max-h-[80vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusTone[viewing.status]}`}>{TESTIMONIAL_STATUS_LABELS[viewing.status]}</span>
                  <h3 className="text-xl font-bold text-gray-900 mt-2">{viewing.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {viewing.isAnonymous ? 'Anonymous' : (viewing.authorName ?? 'No name')}
                    {viewing.authorRole && ` · ${TESTIMONIAL_ROLE_LABELS[viewing.authorRole]}`}
                    {viewing.location && !viewing.isAnonymous ? ` · ${viewing.location}` : ''}
                  </p>
                </div>
                <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-gray-700" aria-label="Close">✕</button>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap border-t border-gray-100 pt-3">{viewing.body}</p>
              {viewing.adminNotes && <p className="mt-4 text-xs text-orange-700 bg-orange-50 p-2 rounded">Admin notes: {viewing.adminNotes}</p>}
            </div>
          </div>
        )}

        <TranslationEditorModal
          open={translateId !== null}
          basePath="/testimonials"
          id={translateId}
          itemLabel="story"
          fields={TRANSLATE_FIELDS}
          canonical={translateId !== null ? (items.find((t) => t.id === translateId) as unknown as Partial<TranslationRow> | undefined) : undefined}
          onClose={() => setTranslateId(null)}
        />
      </div>
    </GovLayout>
  )
})

TestimonialsReview.displayName = 'TestimonialsReview'
export default TestimonialsReview